import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import AuthContext from '../context/AuthContext';
import OnboardingDocuments from './OnboardingDocuments';
import {
  Plus, Search, Users, Mail, Phone, Shield, Eye, EyeOff, Key, Copy, RefreshCw, Edit, UserX, UserCheck, AlertTriangle, Zap, FileSignature,
} from 'lucide-react';

const ROLES = [
  { value: 'department_admin', label: 'Company Admin' },
  { value: 'field_investigator', label: 'Field Investigator' },
  { value: 'skip_tracer', label: 'Skip Tracer' },
  { value: 'transporter', label: 'Transporter' },
];

const ROLE_COLORS = {
  department_admin: 'bg-amber-500/20 text-amber-400',
  field_investigator: 'bg-blue-500/20 text-blue-400',
  skip_tracer: 'bg-purple-500/20 text-purple-400',
  transporter: 'bg-cyan-500/20 text-cyan-400',
};

const TeamManagement = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('nexus_token');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: '', password: '', email: '', name: '', phone: '',
    role: 'field_investigator', title: '', department: '', badge_number: ''
  });
  const pageTitle = 'Nexus Investigators';

  // Emergency reset state (unstick a stuck user with one click)
  const [resetTarget, setResetTarget] = useState(null);   // user object being reset
  const [resetNewUsername, setResetNewUsername] = useState('');
  const [resetCustomPassword, setResetCustomPassword] = useState('');
  const [resetResult, setResetResult] = useState(null);   // { username, new_password }
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/investigators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleCreate = async () => {
    setSubmitError('');
    if (!form.username || !form.password || !form.email || !form.name) {
      setSubmitError('Username, password, email and name are required');
      return;
    }
    setSubmitBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, company_id: user.company_id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      // ✅ 200 OK confirmed — refresh parent cache
      await fetchUsers();
      toast({ title: 'Team member created', description: `${form.name} can now sign in with the provided password.` });
      setForm({ username: '', password: '', email: '', name: '', phone: '', role: 'field_investigator', title: '', department: '', badge_number: '' });
    } catch (e) {
      setSubmitError(e.message || 'Failed to create user');
    } finally {
      setSubmitBusy(false);
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast({ title: 'Success', description: `User ${newStatus.toLowerCase()}` });
        fetchUsers();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const openResetDialog = (u) => {
    setResetTarget(u);
    setResetNewUsername('');
    setResetCustomPassword('');
    setResetResult(null);
    setResetError('');
  };

  const runEmergencyReset = async () => {
    setResetError('');
    setResetBusy(true);
    try {
      const payload = {
        clear_setup_flags: true,
        reactivate: true,
      };
      if (resetCustomPassword.trim()) payload.new_password = resetCustomPassword.trim();
      if (resetNewUsername.trim()) payload.new_username = resetNewUsername.trim();

      const res = await fetch(`${BACKEND_URL}/api/admin/users/${resetTarget.id}/emergency-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setResetResult({ username: data.username, new_password: data.new_password });
      await fetchUsers();
    } catch (e) {
      setResetError(e.message || 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied', description: 'Password copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select and copy manually.', variant: 'destructive' });
    }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="team-management">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{pageTitle}</h1>
          <p className="text-slate-500">Manage your company's investigators, staff, and their onboarding documents</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="border-slate-600 text-slate-300" onClick={fetchUsers}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {['department_admin', 'main_hub', 'super_admin'].includes(user?.role) && (
            <Button variant="outline" className="border-cyan-500/40 !text-cyan-300" onClick={() => navigate('/team/activity')} data-testid="open-activity-log-btn">
              <Zap className="w-4 h-4 mr-2" /> Activity Log
            </Button>
          )}
          <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={() => navigate('/team/new')} data-testid="add-team-member-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Investigator
          </Button>
        </div>
      </div>

      <Tabs defaultValue="roster" className="w-full">
        <TabsList className="bg-[#131A2A] border border-[#334155]">
          <TabsTrigger value="roster" data-testid="tab-investigators-roster">
            <Users className="w-4 h-4 mr-1" /> Investigator Roster
          </TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-investigators-onboarding">
            <FileSignature className="w-4 h-4 mr-1" /> Onboarding Docs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="roster" className="mt-4">

      {/* Search */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search team members..." data-testid="team-search" />
            </div>
            <div className="text-sm text-slate-500">{filtered.length} members</div>
          </div>
        </CardContent>
      </Card>

      {/* User List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading team...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">No team members found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(u => (
            <Card key={u.id} className="bg-white border-slate-200 hover:border-slate-600 transition-colors" data-testid={`team-member-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-slate-800 font-medium">{u.name}</h3>
                      <p className="text-xs text-slate-500">@{u.username}</p>
                    </div>
                  </div>
                  <Badge className={`${u.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} text-xs`}>
                    {u.status}
                  </Badge>
                </div>
                <div className="space-y-1 mb-3">
                  <Badge className={`${ROLE_COLORS[u.role] || 'bg-slate-500/20 text-slate-500'} text-xs`}>
                    {u.role?.replace(/_/g, ' ')}
                  </Badge>
                  {u.title && <p className="text-xs text-slate-500">{u.title}</p>}
                  <div className="flex items-center gap-1 text-xs text-slate-500"><Mail className="w-3 h-3" /> {u.email}</div>
                  {u.phone && <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="w-3 h-3" /> {u.phone}</div>}
                  {u.badge_number && <div className="flex items-center gap-1 text-xs text-slate-500"><Key className="w-3 h-3" /> {u.badge_number}</div>}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                  <button onClick={() => navigate(`/team/${u.id}/edit`)} className="text-slate-500 hover:text-amber-400 p-1" data-testid={`edit-team-member-${u.id}`}>
                    <Edit className="w-4 h-4" />
                  </button>
                  {u.id !== user?.id && (
                    <button
                      onClick={() => openResetDialog(u)}
                      title="Emergency password reset — unstick this user immediately"
                      className="text-slate-500 hover:text-amber-500 p-1"
                      data-testid={`emergency-reset-${u.id}`}
                    >
                      <Zap className="w-4 h-4" />
                    </button>
                  )}
                  {u.id !== user?.id && (
                    <button onClick={() => toggleStatus(u.id, u.status)}
                      className={`p-1 ${u.status === 'ACTIVE' ? 'text-slate-500 hover:text-red-400' : 'text-slate-500 hover:text-green-400'}`}
                      data-testid={`toggle-team-status-${u.id}`}>
                      {u.status === 'ACTIVE' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>
        <TabsContent value="onboarding" className="mt-4">
          <OnboardingDocuments />
        </TabsContent>
      </Tabs>

      {/* Emergency Reset Dialog (small confirm-style — kept intentionally per Phase B scope) === */}
      <Dialog
        open={!!resetTarget}
        onOpenChange={(open) => {
          if (resetBusy) return;
          if (!open) { setResetTarget(null); setResetResult(null); setResetError(''); }
        }}
      >
        <DialogContent className="bg-white border-amber-300 text-slate-900 max-w-md" data-testid="emergency-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> Emergency Password Reset
            </DialogTitle>
          </DialogHeader>

          {!resetResult ? (
            <div className="space-y-4 mt-2 text-sm">
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-900">
                <p className="font-semibold">Unstick {resetTarget?.name} ({resetTarget?.username}).</p>
                <p className="text-xs mt-1 leading-relaxed">
                  This generates a new password, marks the account ACTIVE, clears the forced-setup flags, and unlocks the account.
                  Share the new password with the user via text/phone. They will log straight in with no setup screen.
                </p>
              </div>

              <div>
                <Label>New Username (optional — only if they got stuck on the NEXUS-XXXX handle)</Label>
                <Input
                  value={resetNewUsername}
                  onChange={(e) => setResetNewUsername(e.target.value)}
                  placeholder={resetTarget?.username}
                  className="bg-slate-50 border-slate-300 text-slate-900"
                  data-testid="reset-new-username-input"
                />
              </div>

              <div>
                <Label>Custom Password (optional — leave empty for auto-generated)</Label>
                <Input
                  type="text"
                  value={resetCustomPassword}
                  onChange={(e) => setResetCustomPassword(e.target.value)}
                  placeholder="Leave empty to auto-generate"
                  className="bg-slate-50 border-slate-300 text-slate-900"
                  data-testid="reset-custom-password-input"
                />
              </div>

              {resetError && (
                <div className="px-3 py-2 rounded border border-red-300 bg-red-50 text-sm text-red-800" data-testid="emergency-reset-error">
                  {resetError}
                </div>
              )}

              <Button
                onClick={runEmergencyReset}
                disabled={resetBusy}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                data-testid="confirm-emergency-reset"
              >
                {resetBusy ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Resetting…</>) : (<><Zap className="w-4 h-4 mr-2" />Reset & Unstick</>)}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-2 text-sm" data-testid="emergency-reset-result">
              <div className="rounded border border-green-300 bg-green-50 p-3 text-green-900">
                <p className="font-semibold flex items-center gap-2"><UserCheck className="w-4 h-4" /> Account unstuck</p>
                <p className="text-xs mt-1">Share these credentials with the user. The password is shown ONCE.</p>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">Username</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-slate-100 border border-slate-300 rounded text-slate-900 font-mono text-sm" data-testid="reset-result-username">
                      {resetResult.username}
                    </code>
                    <Button variant="ghost" className="border border-slate-300" onClick={() => copyToClipboard(resetResult.username)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">New Password (shown ONCE)</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-amber-100 border border-amber-300 rounded text-amber-900 font-mono text-sm" data-testid="reset-result-password">
                      {resetResult.new_password}
                    </code>
                    <Button variant="ghost" className="border border-slate-300" onClick={() => copyToClipboard(resetResult.new_password)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <Button onClick={() => { setResetTarget(null); setResetResult(null); }} className="w-full" data-testid="reset-done-btn">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
